import { type UserMetadata } from '@choco-admin/types';
import React, { useState, useCallback } from 'react';

import {
  EntitySearchModal,
  EntitySearchOption,
} from '../../../../../../../components/FormComponents';
import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useChatUserOperations } from '../../../../../hooks';
import { useSearchBuyerUsers } from '../../../../../hooks/useSearchBuyerUsersForChat';
import { useChatPageContext } from '../../../contexts';

interface AddBuyerUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatId: string;
  buyerId: string;
}

export const AddBuyerUserModal: React.FC<AddBuyerUserModalProps> = ({
  isOpen,
  onClose,
  chatId,
  buyerId,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const { updateChatData } = useChatPageContext();
  const { addNotification } = useNotification();

  const { addUsers } = useChatUserOperations();

  const {
    users,
    isLoading: searchLoading,
    error: searchError,
    searchUsers,
  } = useSearchBuyerUsers({
    buyerId,
  });

  const handleSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.trim()) {
        searchUsers(searchTerm.trim());
      }
    },
    [searchUsers]
  );

  const handleConfirm = async (selectedEntity: EntitySearchOption) => {
    setError(null);
    setIsAdding(true);

    try {
      const updatedUsers = await addUsers([
        {
          chatId,
          userId: selectedEntity.id,
          userType: 'buyer',
          operation: 'add',
        },
      ]);

      // Write the server-true user list from the mutation response so the new
      // user appears in the list immediately.
      updateChatData((prevData) =>
        prevData ? { ...prevData, users: updatedUsers } : prevData
      );

      addNotification({
        title: 'User Added',
        message: 'User has been successfully added to the chat.',
        type: 'success',
      });
      onClose();
    } catch (confirmError) {
      addNotification({
        title: 'Add Failed',
        message:
          confirmError instanceof Error
            ? confirmError.message
            : 'Failed to add user to chat.',
        type: 'error',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  return (
    <EntitySearchModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Buyer User"
      searchPlaceholder="Search buyer users by name or email..."
      entityType="buyer user"
      isSearching={searchLoading}
      emptyMessage={
        users.length === 0 && !searchLoading && !searchError
          ? 'The selected buyer has no buyer users'
          : undefined
      }
      searchResults={users.map((user) => ({
        id: user.value,
        name: user.label,
        label: user.label,
        subtitle: (user.metaData as UserMetadata)?.email || '',
        metadata: {
          email: (user.metaData as UserMetadata)?.email || '',
          phone: (user.metaData as UserMetadata)?.phone || '',
          whatsApp:
            (user.metaData as UserMetadata)?.orderPreferences?.some(
              (pref: { type: string }) => pref.type === 'whatsapp'
            ) || false,
          sms:
            (user.metaData as UserMetadata)?.orderPreferences?.some(
              (pref: { type: string }) => pref.type === 'sms'
            ) || false,
          fax:
            (user.metaData as UserMetadata)?.orderPreferences?.some(
              (pref: { type: string }) => pref.type === 'fax'
            ) || false,
        },
      }))}
      onSearch={handleSearch}
      onConfirm={handleConfirm}
      isConfirming={isAdding}
      error={error ?? (searchError ? 'Could not load buyer users.' : null)}
      onErrorDismiss={handleErrorDismiss}
    />
  );
};
